import {Trans} from '@lingui/macro';
import React, {ReactNode, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {hasProperty, isDefined, OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useConstant} from '../../../../../srcCommon/helpers/hookHelpers';
import {getExpressionTypeDropdownIcon} from '../../../editors/expressions/expressionIcons';
import {Expression, isExpression} from '../../../editors/expressions/expressionModel';
import {ExpressionRef, isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {ExpressionPill} from '../inputs/expressionPill';
import {
  MultipleGroupsDropdownBox,
  MultipleGroupsDropdownBoxBaseProps,
  MultipleGroupsDropdownBoxWithAnyHeaderProps,
  MultipleGroupsDropdownBoxWithoutAnyHeaderProps,
} from './multipleGroupsDropdownBox';
import {DropdownGroup} from './multipleGroupsDropdownHelpers';

type PartialMultipleGroupsDropdownBoxBaseProps<T> = Omit<
  MultipleGroupsDropdownBoxBaseProps<T>,
  'groups' | 'onChange' | 'selectedItems'
>;

export interface LikeId {
  id: unknown;
}

/**
 * TAdditionalGroupItems represents additional types for items in additional groups.
 * A selected item can have type id | expression_ref | any additional groups provided.
 */
export type SelectedDropdownItem<T extends LikeId, TAdditionalGroupItems extends unknown[] = []> =
  | T['id']
  | ExpressionRef
  | TAdditionalGroupItems[number];

type AdditionalGroups<TAdditionalGroupItems extends unknown[]> = TAdditionalGroupItems extends [
  infer U,
  ...infer V,
]
  ? readonly [DropdownGroup<U>, ...AdditionalGroups<V>]
  : readonly [];

export interface DropdownWithExpressionsBaseProps<
  T extends LikeId,
  TAdditionalGroupItems extends unknown[] = [],
> extends PartialMultipleGroupsDropdownBoxBaseProps<T> {
  items: ReadonlyArray<T>;
  extraItems?: ReadonlyArray<T>;
  disabledItems?: ReadonlyArray<T>;
  expressions?: OrNull<ReadonlyArray<Expression>>;
  additionalGroups?: AdditionalGroups<TAdditionalGroupItems>;
  selectedItems: OrNull<ReadonlyArray<SelectedDropdownItem<T, TAdditionalGroupItems>>>;
  title?: ReactNode;
  getItemKey: (item: T) => string;
  renderItemLabel: (item: T, isInsideValue?: boolean) => ReactNode;
  renderItemIcon?: (item: T, isInsideValue?: boolean) => ReactNode;
  renderItemTooltip?: RendererOf<T>;
  serializeItemForSearch: (item: T) => string;
  onChange: HandlerOf<OrNull<ReadonlyArray<SelectedDropdownItem<T, TAdditionalGroupItems>>>>;
}

export type DropdownWithExpressionsProps<
  T extends LikeId,
  TAdditionalGroupItems extends unknown[] = [],
> = DropdownWithExpressionsBaseProps<T, TAdditionalGroupItems> &
  (MultipleGroupsDropdownBoxWithoutAnyHeaderProps<T> | MultipleGroupsDropdownBoxWithAnyHeaderProps<T>);

const ItemLabelWrapper = styled.div`
  ${ellipsis()};
`;

export const DropdownWithExpressions = <T extends LikeId, TAdditionalGroupItems extends unknown[] = []>(
  props: DropdownWithExpressionsProps<T, TAdditionalGroupItems>,
) => {
  const onChange = (selectedItems: ReadonlyArray<SelectedDropdownItem<T, TAdditionalGroupItems>>) => {
    const selectedItemsIds = selectedItems.map((item) => {
      if (isExpression(item)) {
        return {expression: item.id};
      }

      if (hasProperty(item, 'id')) {
        return item.id;
      }

      return item;
    });

    // `[]` === any item; `null` === empty selection
    if (selectedItemsIds.length === 0 && !props.canSelectAnyItem) {
      return props.onChange(null);
    }

    return props.onChange(selectedItemsIds);
  };

  const allItems = useMemo((): ReadonlyArray<T | TAdditionalGroupItems[number]> => {
    if (!props.additionalGroups) {
      return props.items;
    }

    return [
      ...props.items,
      ...props.additionalGroups.flatMap(
        (additionalGroup: DropdownGroup<TAdditionalGroupItems[number]>) => additionalGroup.items,
      ),
    ];
  }, [props.additionalGroups, props.items]);

  const {selectedItems, initialSelectedItems} = useResolveSelectedItems(
    props.selectedItems,
    allItems,
    props.extraItems,
    props.expressions,
  );

  const expressionGroup: DropdownGroup<Expression> = {
    items: props.expressions || [],

    title: props.title ? <Trans id="expressions">Dynamic variables</Trans> : undefined,
    renderItemLabel: renderDropdownExpressionLabel,
    renderItemIcon: renderDropdownExpressionIcon,
    getItemKey: (item) => `${item.id}`,
    serializeItemForSearch: (item) => item.name || item.defaultName,
  };

  const groups = [
    expressionGroup,
    ...(props.additionalGroups ?? []),
    {
      items: props.items,
      extraItems: props.extraItems,
      disabledItems: props.disabledItems,
      title: props.title,
      getItemKey: props.getItemKey,
      renderItemLabel: props.renderItemLabel,
      renderItemIcon: props.renderItemIcon,
      renderItemTooltip: props.renderItemTooltip,
      serializeItemForSearch: props.serializeItemForSearch,
    },
  ];

  return (
    <MultipleGroupsDropdownBox<SelectedDropdownItem<T, TAdditionalGroupItems>>
      {...props}
      groups={groups}
      onChange={onChange}
      selectedItems={selectedItems}
      initialSelectedItems={initialSelectedItems}
    />
  );
};

function useResolveSelectedItems<T extends LikeId, TAdditionalGroupItems extends unknown[] = []>(
  selectedItemIds: OrNull<ReadonlyArray<SelectedDropdownItem<T, TAdditionalGroupItems>>>,
  items: ReadonlyArray<T | TAdditionalGroupItems[number]>,
  extraItems?: ReadonlyArray<T>,
  expressions?: OrNull<ReadonlyArray<Expression>>,
) {
  // Display initial select items at the top of the dropdown.
  const initialSelectedItemIds = useConstant(() => selectedItemIds);

  const resolveSelectedItemIds = useCallback(
    (itemsId: OrNull<ReadonlyArray<SelectedDropdownItem<T, TAdditionalGroupItems>>>) =>
      itemsId
        ?.map((item) => {
          if (isExpressionRef(item)) {
            return expressions?.find((exp) => exp.id === item.expression);
          }
          return [...items, ...(extraItems || [])].find((i) => {
            if (hasProperty(i, 'id')) {
              return i.id === item;
            }

            return i === item;
          });
        })
        .filter(isDefined) ?? null,
    [expressions, items, extraItems],
  );

  const initialSelectedItems = useMemo(
    () => resolveSelectedItemIds(initialSelectedItemIds),
    [initialSelectedItemIds, resolveSelectedItemIds],
  );
  const selectedItems = useMemo(
    () => resolveSelectedItemIds(selectedItemIds),
    [selectedItemIds, resolveSelectedItemIds],
  );

  return {
    initialSelectedItems,
    selectedItems,
  };
}

export function renderDropdownExpressionLabel(item: Expression, isInsideValue?: boolean) {
  if (isInsideValue) {
    return <ExpressionPill type={item.inferredType} label={item.name || item.defaultName} />;
  }

  return <ItemLabelWrapper>{item.name || item.defaultName}</ItemLabelWrapper>;
}

export function renderDropdownExpressionIcon(item: Expression, isInsideValue?: boolean) {
  if (isInsideValue) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <Icon color={theme.greys.shade70} name={getExpressionTypeDropdownIcon(item.inferredType)} />
      )}
    </UserThemeConsumer>
  );
}
